import { createFileRoute, Link } from '@tanstack/react-router';
import { ArrowLeft, MousePointerClick, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useSession, useReplayUrl } from '@/hooks/use-session';
import { ReplayPlayer } from '@/components/sessions/replay-player';
import { formatDuration, relativeTime } from '@/lib/format';

export const Route = createFileRoute('/_authenticated/sites/$siteId/sessions/$sessionId')({
  component: SessionDetailPage,
});

function SessionDetailPage() {
  const { siteId, sessionId } = Route.useParams();
  const { data: detail, isLoading } = useSession(sessionId);
  const { data: replay } = useReplayUrl(sessionId, detail?.session.has_replay ?? false);

  if (isLoading || !detail) {
    return <div className="mx-auto max-w-7xl px-6 py-8 text-sm text-text-muted">Loading session…</div>;
  }

  const s = detail.session;

  return (
    <div className="mx-auto max-w-7xl px-6 py-8 animate-fade-up">
      <Button asChild variant="ghost" size="sm" className="mb-4">
        <Link to="/sites/$siteId/sessions" params={{ siteId }}>
          <ArrowLeft className="size-3.5" /> Back to sessions
        </Link>
      </Button>

      <header className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="mb-1 text-xl font-semibold tracking-tight">
            Session <span className="font-mono text-text-muted text-base">{s.id.slice(0, 12)}…</span>
          </h1>
          <div className="flex flex-wrap items-center gap-3 text-xs text-text-muted">
            <span>{relativeTime(s.started_at)}</span>
            <span>·</span>
            <span>{s.device} · {s.browser}</span>
            <span>·</span>
            <span>{s.country}</span>
            {s.duration_ms != null && (
              <>
                <span>·</span>
                <span className="font-mono">{formatDuration(s.duration_ms)}</span>
              </>
            )}
          </div>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {s.flags.rage_click && (
            <Badge variant="destructive" className="gap-1"><MousePointerClick className="size-3" /> Rage click</Badge>
          )}
          {s.flags.dead_click && <Badge variant="outline">Dead click</Badge>}
          {s.flags.quick_back && <Badge variant="secondary">Quick back</Badge>}
          {s.flags.error && (
            <Badge variant="destructive" className="gap-1"><AlertCircle className="size-3" /> Error</Badge>
          )}
        </div>
      </header>

      {s.has_replay && replay ? (
        <ReplayPlayer replayUrl={replay.url} />
      ) : (
        <div className="rounded-lg border border-border bg-bg-surface p-12 text-center text-sm text-text-muted">
          No replay available for this session.
        </div>
      )}

      {detail.insights.length > 0 && (
        <section className="mt-6">
          <h2 className="mb-3 text-sm font-semibold uppercase tracking-wider text-text-muted">Insights detected</h2>
          <div className="rounded-lg border border-border bg-bg-surface divide-y divide-border">
            {detail.insights.map((i) => (
              <div key={i.id} className="p-3 text-xs flex items-center gap-4">
                <Badge variant="outline">{i.type}</Badge>
                <span className="font-mono text-text-muted">{i.url}</span>
                {i.selector && <span className="font-mono text-text-muted">{i.selector}</span>}
                <span className="ml-auto font-mono text-text-subtle">+{i.timestamp_ms}ms</span>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
